/**
 * proofBuffer — §D (Proof Library)'s explicit-save edit buffer.
 *
 * Pure state + coercion for `ProofLibraryPanel`, extracted so the buffering
 * rules are testable without a DOM. Under the explicit-save contract
 * (ai-docs/architect-briefs/bb-phase2-explicit-save-brief-20260810.md) §D's
 * edits no longer reach the wire on blur — they land here, and the section's
 * registered `save()` replays them through the SAME store actions the panel
 * already used (`upsertEntry` / `discardEntry`). Verify stays immediate and
 * never touches this buffer: it is an operator act, not an edit.
 *
 * Two deliberate shapes:
 *
 *  - **Raw, not typed.** Text/number/date fields buffer their as-typed string
 *    so an input can render a mid-typing state ("4.", "") that no typed
 *    payload could hold. Coercion and validation happen once, in
 *    `coerceProofFields`, at save time. Checkboxes buffer booleans because
 *    they have no intermediate state.
 *
 *  - **Diffed against the store, never blind.** `setBufferedField` DROPS a
 *    field whose new value equals the store's current value, so typing an
 *    edit and typing it back leaves the section clean and the "n changes"
 *    count honest. This is also the discipline the §B prod incident asks for:
 *    buffered ops stay id-keyed and are re-diffed at save; nothing is ever
 *    synthesized from a list comparison.
 */
import type { BbTable } from '@/features/brand-bible-wizard/types';

/** The three tables §D is scored on. `credentials` is shared with §A, which
 *  owns the license/insurance kinds — the panel filters, not this module. */
export type ProofTable = Extract<BbTable, 'review_stats' | 'credentials' | 'case_studies'>;

/** As-typed field value: string for text/number/date inputs, boolean for checkboxes. */
export type RawValue = string | boolean;
export type RawFields = Readonly<Record<string, RawValue>>;

export interface ProofCreate {
  /** Client-side id for a row that does not exist server-side yet. */
  tempId: string;
  table: ProofTable;
  fields: RawFields;
}

export interface ProofBuffer {
  /** `${table}:${id}` -> the fields that differ from the stored row. */
  patches: Readonly<Record<string, RawFields>>;
  creates: readonly ProofCreate[];
  /** `${table}:${id}` for stored rows staged for removal. */
  deletes: readonly string[];
}

export const EMPTY_PROOF_BUFFER: ProofBuffer = { patches: {}, creates: [], deletes: [] };

export const rowKey = (table: ProofTable, id: string): string => `${table}:${id}`;

const PENDING_PREFIX = 'pending:';
let pendingCounter = 0;

/** True for a row that only exists in this buffer — it has no server id yet,
 *  so it can never be verified, approved for public use, or hard-deleted. */
export const isPendingId = (id: string): boolean => id.startsWith(PENDING_PREFIX);

/* ------------------------------- validation ------------------------------- */

/** Preserved verbatim from the pre-buffer panel, UTC and all. West of
 *  Greenwich this rolls over early, so the "no future dates" guard below can
 *  accept tomorrow's local date on a table whose whole point is that the
 *  as-of date is the proof. Tracked as a §D fast-follow alongside the blank
 *  review-count defect — this conversion stays behaviour-neutral on what
 *  reaches the wire, and fixing both together keeps that PR reviewable. */
export function todayISODate(): string {
  return new Date().toISOString().slice(0, 10);
}

export function parseReviewCount(raw: string): { value: number } | { error: string } {
  const trimmed = raw.trim();
  // Preserved from the pre-buffer panel: blank means zero here. Writing a
  // claimed "0 reviews" for a blank field is a real defect, tracked as a §D
  // fast-follow — deliberately NOT changed inside the save conversion, which
  // stays behaviour-neutral on what reaches the wire.
  if (!trimmed) return { value: 0 };
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n < 0) return { error: 'Review count must be a whole number, 0 or more.' };
  return { value: n };
}

// '' must map to "no rating", never 0 — a claimed 0.0-star rating is a real,
// wrong claim, not "no rating given". DB is NUMERIC(3,2); an out-of-range
// value 500s as a Postgres overflow, so 0-5 is rejected client-side first.
//
// That absence is `null`, not `undefined`, for the same reason as
// `optionalText` below: `undefined` is dropped by JSON.stringify, so clearing
// a rating that HAD a value silently did nothing. On a create, `null` is the
// column's own default state, so nothing changes there.
export function parseRating(raw: string): { value: number | null } | { error: string } {
  const trimmed = raw.trim();
  if (!trimmed) return { value: null };
  const n = Number(trimmed);
  if (Number.isNaN(n) || n < 0 || n > 5) return { error: 'Rating must be a number between 0 and 5.' };
  return { value: n };
}

/* --------------------------------- reads ---------------------------------- */

export function patchedFields(buffer: ProofBuffer, table: ProofTable, id: string): RawFields | undefined {
  return buffer.patches[rowKey(table, id)];
}

/** The value an input should display: the buffered edit if there is one, else
 *  the stored value. */
export function bufferedField(
  buffer: ProofBuffer,
  table: ProofTable,
  id: string,
  field: string,
  stored: RawValue,
): RawValue {
  if (isPendingId(id)) {
    const create = buffer.creates.find((c) => c.tempId === id);
    return create?.fields[field] ?? stored;
  }
  return patchedFields(buffer, table, id)?.[field] ?? stored;
}

/** A stored row with unsaved edits. Drives the per-row Verify lock (§D is the
 *  only section whose verified branch renders read-only text INSTEAD of the
 *  inputs, so verifying a dirty row would strand the buffered value out of
 *  reach). Pending rows are not "dirty rows" — they are creates. */
export function rowIsDirty(buffer: ProofBuffer, table: ProofTable, id: string): boolean {
  const fields = patchedFields(buffer, table, id);
  return Boolean(fields && Object.keys(fields).length > 0);
}

export function isRemoved(buffer: ProofBuffer, table: ProofTable, id: string): boolean {
  return buffer.deletes.includes(rowKey(table, id));
}

export function pendingCreates(buffer: ProofBuffer, table: ProofTable): ProofCreate[] {
  return buffer.creates.filter((c) => c.table === table);
}

/** Field-level for edits (three fields on one row honestly reads as "3
 *  changes"), one per staged create, one per staged removal. Feeds the
 *  footer's "Save D · {n} changes" label. */
export function changeCount(buffer: ProofBuffer): number {
  const patched = Object.values(buffer.patches).reduce((n, fields) => n + Object.keys(fields).length, 0);
  return patched + buffer.creates.length + buffer.deletes.length;
}

export const isDirty = (buffer: ProofBuffer): boolean => changeCount(buffer) > 0;

/* -------------------------------- mutations ------------------------------- */

/** Buffer one field edit. `stored` is the row's current value in the store —
 *  an edit back to it removes the field rather than recording a no-op change. */
export function setBufferedField(
  buffer: ProofBuffer,
  table: ProofTable,
  id: string,
  field: string,
  next: RawValue,
  stored: RawValue,
): ProofBuffer {
  if (isPendingId(id)) {
    return {
      ...buffer,
      creates: buffer.creates.map((c) =>
        c.tempId === id ? { ...c, fields: { ...c.fields, [field]: next } } : c,
      ),
    };
  }

  const key = rowKey(table, id);
  const current = buffer.patches[key];
  const nextFields: Record<string, RawValue> = { ...current };

  if (next === stored) delete nextFields[field];
  else nextFields[field] = next;

  const patches = { ...buffer.patches };
  if (Object.keys(nextFields).length === 0) delete patches[key];
  else patches[key] = nextFields;

  return { ...buffer, patches };
}

export function stageCreate(buffer: ProofBuffer, table: ProofTable, fields: RawFields): ProofBuffer {
  pendingCounter += 1;
  return {
    ...buffer,
    creates: [...buffer.creates, { tempId: `${PENDING_PREFIX}${table}:${pendingCounter}`, table, fields }],
  };
}

/** Remove staging. A pending row is dropped outright — created and removed in
 *  one buffer session, it never reaches the wire. A stored row is staged for
 *  deletion and its buffered edits are discarded with it (patching a row you
 *  are about to delete is noise, and it would inflate the change count). */
export function stageRemoval(buffer: ProofBuffer, table: ProofTable, id: string): ProofBuffer {
  if (isPendingId(id)) {
    return { ...buffer, creates: buffer.creates.filter((c) => c.tempId !== id) };
  }
  const key = rowKey(table, id);
  if (buffer.deletes.includes(key)) return buffer;
  const patches = { ...buffer.patches };
  delete patches[key];
  return { ...buffer, patches, deletes: [...buffer.deletes, key] };
}

/* -------------------------------- coercion -------------------------------- */

export interface CoercedRow {
  payload: Record<string, unknown>;
  errors: string[];
}

const asString = (v: RawValue | undefined): string => (typeof v === 'string' ? v : '');
const asBool = (v: RawValue | undefined): boolean => v === true;

/** Text columns clear with `undefined`, never `''` — matching the pre-buffer
 *  panel's `next || undefined`. */
/**
 * An emptied text column clears to `null`, never `undefined`.
 *
 * `undefined` was the bug: `JSON.stringify` drops undefined keys, so a PATCH
 * meant to clear a field shipped a body with no such field, the server's
 * merge-patch correctly read that as "don't touch", and the store's response
 * snapped the input back to the old value. The user's clear vanished with a
 * 200 and no error. Verified on prod 2026-08-17 (probe walk §3).
 *
 * `null` rather than `''` — three reasons, in order of weight:
 *  1. `expiry` is DATE and `rating` is NUMERIC(3,2). Neither can hold `''`
 *     (`''::date` errors), so `''` cannot be the uniform answer and a
 *     per-column split would be a second rule to remember.
 *  2. `null` is ALREADY the stored representation of "unset" for every one of
 *     these columns — a row created without them reads back `null`. Clearing
 *     therefore returns the row to the exact state it had before the field was
 *     ever filled, instead of minting a second distinct kind of empty that
 *     every reader would have to treat as equivalent.
 *  3. §C's buffers established `null`-clears already (essentialsBuffer's
 *     `service_radius_miles`, the socials delta), with `''`/`null`/`undefined`
 *     treated as one absence for DIRTY DETECTION only. Both halves match here:
 *     see `setBufferedField`, where clearing a never-filled field compares
 *     equal to its stored `''` and is not recorded as a change at all.
 *
 * Downstream is indifferent: `projection.py`'s `_credential_line` /
 * `_case_study_line` compose on truthiness, so `null` and `''` read the same.
 * That is what makes 1-3 decisive rather than merely convenient.
 */
const optionalText = (v: RawValue | undefined): string | null => asString(v) || null;

/**
 * Turn one row's buffered raw fields into the payload `upsertEntry` takes,
 * collecting human-readable validation errors instead of throwing. A row with
 * any error is not sent; `save()` reports the errors and keeps the buffer.
 *
 * `label` names the row in error text ("Rating for Google" reads better than
 * "Rating"), and is the row's platform / credential name / job type.
 */
export function coerceProofFields(table: ProofTable, fields: RawFields, label: string): CoercedRow {
  const payload: Record<string, unknown> = {};
  const errors: string[] = [];
  const suffix = label ? ` (${label})` : '';

  if ('approved_for_public' in fields) payload.approved_for_public = asBool(fields.approved_for_public);

  if (table === 'review_stats') {
    if ('platform' in fields) payload.platform = asString(fields.platform).trim();
    if ('review_count' in fields) {
      const parsed = parseReviewCount(asString(fields.review_count));
      if ('error' in parsed) errors.push(`${parsed.error}${suffix}`);
      else payload.review_count = parsed.value;
    }
    if ('rating' in fields) {
      const parsed = parseRating(asString(fields.rating));
      if ('error' in parsed) errors.push(`${parsed.error}${suffix}`);
      else payload.rating = parsed.value;
    }
    if ('as_of' in fields) {
      const asOf = asString(fields.as_of);
      if (!asOf) errors.push(`Date is required — the as-of date is the proof.${suffix}`);
      else if (asOf > todayISODate()) errors.push(`Date cannot be in the future.${suffix}`);
      else payload.as_of = asOf;
    }
    return { payload, errors };
  }

  if (table === 'credentials') {
    if ('kind' in fields) payload.kind = asString(fields.kind);
    if ('name' in fields) payload.name = asString(fields.name).trim();
    for (const field of ['number', 'issuer_or_state', 'expiry', 'proof_ref'] as const) {
      if (field in fields) payload[field] = optionalText(fields[field]);
    }
    return { payload, errors };
  }

  if ('job_type' in fields) payload.job_type = asString(fields.job_type).trim();
  if ('client_permission' in fields) payload.client_permission = asBool(fields.client_permission);
  for (const field of ['result', 'approved_quote', 'metro'] as const) {
    if (field in fields) payload[field] = optionalText(fields[field]);
  }
  return { payload, errors };
}
